import React, { useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Section } from "@/components/bi/InspectorBits";
import { CONTAINER_LAYOUTS, TAB_POSITIONS, TAB_STYLES, containerTabs } from "@/components/bi/ContainerWidget";
import { getWidgetMeta } from "@/lib/widgetCatalog";
import { Boxes, ArrowUp, ArrowDown, LogOut, Pencil, Lock, LockOpen, Eye, EyeOff, AlignStartVertical, StretchHorizontal, StretchVertical, LayoutGrid, Paintbrush, Layers, Plus, X } from "lucide-react";
import { ContainerChildStyle, childStylePatch } from "@/components/bi/ContainerChildStyle";

// Inspector section for Combine Widget (container): layout mode, spacing, arrange tools, children.
export function InspectorContainerSection({ ins, widgets = [], onReleaseChild, onReorderChildren, onChildLayout, onUpdateWidget }) {
  const { widget, config, patchConfig } = ins;
  const cc = config.container || {};
  const patchCC = (p) => patchConfig({ container: { ...cc, ...p } });
  const mode = cc.layout || "free";
  const [renaming, setRenaming] = useState(null);
  const [styling, setStyling] = useState(null);
  const kids = widgets
    .filter((w) => w.parent_id === widget.id)
    .sort((a, b) => ((a.layout_child?.order ?? a.layout_child?.y ?? 0) - (b.layout_child?.order ?? b.layout_child?.y ?? 0)));
  const flow = ["vertical", "horizontal", "responsive"].includes(mode);
  const tabs = containerTabs(widget);
  const newTabId = () => `ct${Math.random().toString(36).slice(2, 7)}`;
  const swap = (arr, i, j) => { const out = [...arr]; [out[i], out[j]] = [out[j], out[i]]; return out; };
  const gridLike = ["free", "grid"].includes(mode);

  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= kids.length) return;
    const order = kids.map((k) => k.id);
    [order[i], order[j]] = [order[j], order[i]];
    onReorderChildren && onReorderChildren(Object.fromEntries(order.map((id, n) => [id, n])));
  };

  // Item 77.5: arrange tools — align left / distribute H / distribute V / auto-fit grid
  const arrange = (kind) => {
    if (!onChildLayout || !kids.length) return;
    const map = {};
    if (kind === "left") kids.forEach((k) => { const l = k.layout_child || {}; map[k.id] = { ...l, x: 0 }; });
    if (kind === "hdist") {
      const w = Math.max(1, Math.floor(12 / kids.length));
      kids.forEach((k, i) => {
        const l = k.layout_child || {};
        map[k.id] = { ...l, x: i * w, y: 0, w: i === kids.length - 1 ? 12 - w * (kids.length - 1) : w };
      });
    }
    if (kind === "vdist") {
      let y = 0;
      kids.forEach((k) => { const l = k.layout_child || {}; map[k.id] = { ...l, x: 0, y, w: l.w || 12 }; y += l.h || 4; });
    }
    if (kind === "fit") {
      const n = kids.length, colsN = Math.ceil(Math.sqrt(n)), w = Math.max(1, Math.floor(12 / colsN));
      const h = Math.max(2, Math.round(kids.reduce((a, k) => a + (k.layout_child?.h || 4), 0) / n));
      kids.forEach((k, i) => {
        const c = i % colsN, r = Math.floor(i / colsN);
        map[k.id] = { x: c * w, y: r * h, w: c === colsN - 1 ? 12 - w * (colsN - 1) : w, h };
      });
    }
    onChildLayout(map);
  };

  return (
    <Section value="container" icon={Boxes} title="Container Layout" testid="inspector-sec-container"
      hint="How widgets inside this container are arranged.">
      <div>
        <Label className="text-xs text-[color:var(--ink-3)]">Layout mode</Label>
        <Select value={mode} onValueChange={(v) => patchCC({ layout: v })}>
          <SelectTrigger data-testid="container-layout-select" className="mt-1.5 w-full bg-[color:var(--surface)]"><SelectValue /></SelectTrigger>
          <SelectContent>{CONTAINER_LAYOUTS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <div className="flex gap-1.5">
        <div className="flex-1">
          <Label className="text-[10px] text-[color:var(--ink-3)]">Gap (px)</Label>
          <Input data-testid="container-gap" type="number" min={0} max={48} value={cc.gap ?? 8}
            onChange={(e) => patchCC({ gap: Math.max(0, Number(e.target.value) || 0) })} className="mt-0.5 bg-[color:var(--surface)] h-8 text-xs" />
        </div>
        <div className="flex-1">
          <Label className="text-[10px] text-[color:var(--ink-3)]">Padding (px)</Label>
          <Input data-testid="container-padding" type="number" min={0} max={48} value={cc.padding ?? 8}
            onChange={(e) => patchCC({ padding: Math.max(0, Number(e.target.value) || 0) })} className="mt-0.5 bg-[color:var(--surface)] h-8 text-xs" />
        </div>
        {mode === "responsive" && (
          <div className="flex-1">
            <Label className="text-[10px] text-[color:var(--ink-3)]">Min col (px)</Label>
            <Input data-testid="container-minw" type="number" min={120} max={640} value={cc.min_w ?? 240}
              onChange={(e) => patchCC({ min_w: Math.max(120, Number(e.target.value) || 240) })} className="mt-0.5 bg-[color:var(--surface)] h-8 text-xs" />
          </div>
        )}
        {["free", "grid", "responsive"].includes(mode) && (
          <div className="flex-1">
            <Label className="text-[10px] text-[color:var(--ink-3)]">Row (px)</Label>
            <Input data-testid="container-rowh" type="number" min={24} max={400} value={cc.row_h ?? (mode === "responsive" ? 200 : 40)}
              onChange={(e) => patchCC({ row_h: Math.max(24, Number(e.target.value) || 40) })} className="mt-0.5 bg-[color:var(--surface)] h-8 text-xs" />
          </div>
        )}
      </div>
      {mode === "free" && (
        <div className="flex items-center justify-between py-0.5">
          <Label className="text-xs text-[color:var(--ink-2)]">Allow overlapping widgets</Label>
          <Switch data-testid="container-overlap" checked={!!cc.overlap} onCheckedChange={(v) => patchCC({ overlap: v })} />
        </div>
      )}
      {/* Item 27: Tab Widget — turn this container into a tab set */}
      <div className="pt-2 border-t border-[color:var(--glass-border)] space-y-1.5">
        <div className="flex items-center justify-between py-0.5">
          <Label className="text-xs text-[color:var(--ink-2)] flex items-center gap-1.5"><Layers className="w-3.5 h-3.5" />Tabs inside this widget</Label>
          <Switch data-testid="container-tabs-enable" checked={tabs.length > 0}
            onCheckedChange={(v) => patchCC({ tabs: v ? [{ id: newTabId(), name: "Tab 1" }, { id: newTabId(), name: "Tab 2" }] : [] })} />
        </div>
        {tabs.length > 0 && (
          <>
            <div className="flex gap-1.5">
              <div className="flex-1">
                <Label className="text-[10px] text-[color:var(--ink-3)]">Tab position</Label>
                <Select value={cc.tab_pos || "top"} onValueChange={(v) => patchCC({ tab_pos: v })}>
                  <SelectTrigger data-testid="container-tab-pos" className="mt-0.5 bg-[color:var(--surface)] h-8 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>{TAB_POSITIONS.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="flex-1">
                <Label className="text-[10px] text-[color:var(--ink-3)]">Tab style</Label>
                <Select value={cc.tab_style || "underline"} onValueChange={(v) => patchCC({ tab_style: v })}>
                  <SelectTrigger data-testid="container-tab-style" className="mt-0.5 bg-[color:var(--surface)] h-8 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>{TAB_STYLES.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-1">
              {tabs.map((t, i) => (
                <div key={t.id} data-testid={`container-tab-row-${i}`} className="flex items-center gap-1 rounded-lg bg-[color:var(--surface-2)] px-2 py-1">
                  <Input data-testid={`container-tab-name-${i}`} value={t.name}
                    onChange={(e) => patchCC({ tabs: tabs.map((x, j) => (j === i ? { ...x, name: e.target.value.slice(0, 40) } : x)) })}
                    className="h-6 text-[11px] bg-[color:var(--surface)] flex-1 min-w-0" />
                  <button data-testid={`container-tab-up-${i}`} disabled={i === 0} onClick={() => patchCC({ tabs: swap(tabs, i, i - 1) })}
                    className="p-0.5 text-[color:var(--ink-3)] hover:text-[color:var(--ink)] disabled:opacity-30"><ArrowUp className="w-3 h-3" /></button>
                  <button data-testid={`container-tab-down-${i}`} disabled={i === tabs.length - 1} onClick={() => patchCC({ tabs: swap(tabs, i, i + 1) })}
                    className="p-0.5 text-[color:var(--ink-3)] hover:text-[color:var(--ink)] disabled:opacity-30"><ArrowDown className="w-3 h-3" /></button>
                  <button data-testid={`container-tab-remove-${i}`} disabled={tabs.length <= 1}
                    onClick={() => {
                      const gone = tabs[i].id;
                      patchCC({ tabs: tabs.filter((_, j) => j !== i) });
                      kids.filter((k) => (k.layout_child?.tab || "") === gone).forEach((k) => {
                        onUpdateWidget && onUpdateWidget(k.id, { layout_child: { ...(k.layout_child || {}), tab: "" } });
                      });
                    }}
                    className="p-0.5 text-[color:var(--ink-3)] hover:text-[color:var(--danger)] disabled:opacity-30"><X className="w-3 h-3" /></button>
                </div>
              ))}
              <Button data-testid="container-tab-add" size="sm" variant="outline" className="w-full h-7 text-xs bg-[color:var(--surface)]"
                onClick={() => patchCC({ tabs: [...tabs, { id: newTabId(), name: `Tab ${tabs.length + 1}` }] })}>
                <Plus className="w-3 h-3 mr-1" />Add tab
              </Button>
            </div>
            <p className="text-[10px] text-[color:var(--ink-3)]">Only the open tab is loaded. The open tab is kept in the URL so links reopen it.</p>
          </>
        )}
      </div>
      {gridLike && kids.length > 1 && (
        <div>
          <Label className="text-[10px] text-[color:var(--ink-3)]">Arrange</Label>
          <div className="flex gap-1 mt-1">
            <Button data-testid="container-align-left" size="sm" variant="outline" title="Align left" className="h-7 px-2 bg-[color:var(--surface)]" onClick={() => arrange("left")}><AlignStartVertical className="w-3.5 h-3.5" /></Button>
            <Button data-testid="container-dist-h" size="sm" variant="outline" title="Distribute horizontally" className="h-7 px-2 bg-[color:var(--surface)]" onClick={() => arrange("hdist")}><StretchHorizontal className="w-3.5 h-3.5" /></Button>
            <Button data-testid="container-dist-v" size="sm" variant="outline" title="Distribute vertically" className="h-7 px-2 bg-[color:var(--surface)]" onClick={() => arrange("vdist")}><StretchVertical className="w-3.5 h-3.5" /></Button>
            <Button data-testid="container-auto-fit" size="sm" variant="outline" title="Auto fit (even grid)" className="h-7 px-2 bg-[color:var(--surface)]" onClick={() => arrange("fit")}><LayoutGrid className="w-3.5 h-3.5" /></Button>
          </div>
        </div>
      )}
      <p className="text-[10px] text-[color:var(--ink-3)]">Background, border, radius, shadow and transparency live in the <b>Background</b> section.</p>
      <div className="pt-2 border-t border-[color:var(--glass-border)]">
        <Label className="text-xs text-[color:var(--ink-3)]">Widgets inside ({kids.length})</Label>
        <div className="space-y-1 mt-1.5">
          {kids.map((k, i) => {
            const meta = getWidgetMeta(k.type);
            return (
              <React.Fragment key={k.id}>
              <div data-testid="container-child-row"
                className={"flex items-center gap-1 rounded-lg bg-[color:var(--surface-2)] px-2 py-1.5" + (k.hidden ? " opacity-50" : "")}>
                <meta.icon className="w-3 h-3 shrink-0 text-[color:var(--brand-ocean)]" />
                {renaming === k.id ? (
                  <Input autoFocus defaultValue={k.title || meta.label} data-testid="container-child-rename-input"
                    onBlur={(e) => { const v = e.target.value.trim(); if (v && onUpdateWidget) onUpdateWidget(k.id, { title: v }); setRenaming(null); }}
                    onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); if (e.key === "Escape") setRenaming(null); }}
                    className="h-6 text-[11px] bg-[color:var(--surface)] flex-1 min-w-0" />
                ) : (
                  <span className="text-[11px] text-[color:var(--ink-2)] truncate flex-1">{k.title || meta.label}</span>
                )}
                {flow && (
                  <>
                    <button data-testid={`container-child-up-${i}`} onClick={() => move(i, -1)} disabled={i === 0}
                      className="p-0.5 text-[color:var(--ink-3)] hover:text-[color:var(--ink)] disabled:opacity-30"><ArrowUp className="w-3 h-3" /></button>
                    <button data-testid={`container-child-down-${i}`} onClick={() => move(i, 1)} disabled={i === kids.length - 1}
                      className="p-0.5 text-[color:var(--ink-3)] hover:text-[color:var(--ink)] disabled:opacity-30"><ArrowDown className="w-3 h-3" /></button>
                  </>
                )}
                {tabs.length > 0 && (
                  <select data-testid={`container-child-tab-${i}`} value={k.layout_child?.tab || ""}
                    onChange={(e) => onUpdateWidget && onUpdateWidget(k.id, { layout_child: { ...(k.layout_child || {}), tab: e.target.value } })}
                    className="h-6 text-[10px] rounded border border-[color:var(--glass-border)] bg-[color:var(--surface)] max-w-[88px]">
                    <option value="">{tabs[0].name} (default)</option>
                    {tabs.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                  </select>
                )}
                <button data-testid="container-child-rename" title="Rename" onClick={() => setRenaming(k.id)}
                  className="p-0.5 text-[color:var(--ink-3)] hover:text-[color:var(--ink)]"><Pencil className="w-3 h-3" /></button>
                <button data-testid="container-child-lock" title={k.locked ? "Unlock" : "Lock position"} onClick={() => onUpdateWidget && onUpdateWidget(k.id, { locked: !k.locked })}
                  className={"p-0.5 hover:text-[color:var(--ink)] " + (k.locked ? "text-[color:var(--brand-ocean)]" : "text-[color:var(--ink-3)]")}>
                  {k.locked ? <Lock className="w-3 h-3" /> : <LockOpen className="w-3 h-3" />}
                </button>
                <button data-testid="container-child-hide" title={k.hidden ? "Show" : "Hide"} onClick={() => onUpdateWidget && onUpdateWidget(k.id, { hidden: !k.hidden })}
                  className={"p-0.5 hover:text-[color:var(--ink)] " + (k.hidden ? "text-[color:var(--brand-ocean)]" : "text-[color:var(--ink-3)]")}>
                  {k.hidden ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
                </button>
                <button data-testid="container-child-style" title="Format this widget (title, background, border)" aria-expanded={styling === k.id} onClick={() => setStyling(styling === k.id ? null : k.id)}
                  className={"p-0.5 hover:text-[color:var(--ink)] " + (styling === k.id ? "text-[color:var(--brand-ocean)]" : "text-[color:var(--ink-3)]")}><Paintbrush className="w-3 h-3" /></button>
                <button data-testid="container-child-release" title="Release to canvas" onClick={() => onReleaseChild && onReleaseChild(k.id)}
                  className="p-0.5 text-[color:var(--ink-3)] hover:text-[color:var(--brand-ocean)]"><LogOut className="w-3 h-3" /></button>
              </div>
              {styling === k.id && onUpdateWidget && (
                <ContainerChildStyle child={k} testid={`container-child-style-panel`} onPatch={(patch) => onUpdateWidget(k.id, patch)}
                  onApplyToAll={(style) => onUpdateWidget(Object.fromEntries(kids.map((other) => [other.id, childStylePatch(other, style)])))} />
              )}
              </React.Fragment>
            );
          })}
          {!kids.length && <p className="text-[11px] text-[color:var(--ink-3)]">No widgets inside yet — drop widgets on the container, or select some and press <b>Combine</b>.</p>}
        </div>
        {kids.length > 0 && (
          <Button data-testid="container-release-all" size="sm" variant="outline" className="mt-2 w-full h-7 text-xs bg-[color:var(--surface)]"
            onClick={() => onReleaseChild && onReleaseChild(kids.map((k) => k.id))}>
            <LogOut className="w-3 h-3 mr-1" />Release all to canvas
          </Button>
        )}
      </div>
    </Section>
  );
}
